// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF

import { useAuthContext } from '@tls/auth';
import { MenuItemType } from '@trumpf/ux.application-framework';
import {
  UxcAppHeaderHelpMenu,
  UxcAppHeaderNeo,
  UxcAppHeaderSettingsMenu,
  UxcAppHeaderUserProfile,
  UxcAppLauncher,
  UxcMenuItem,
} from '@trumpf/ux.application-framework-react';
import AppBanner from 'components/AppBanner';
import InitializationBanner from 'components/InitializationBanner';
import CustomerContractsDialog from 'components/settings/Contracts/CustomerContractsDialog';
import { NavbarContext, NavbarContextType } from 'components/treactui-template/organisms/page/NavbarContext';
import useDownloadFileApi from 'hooks/useDownloadApi';
import { useNoiseConfig } from 'hooks/useNoiseConfig';
import { useAppStatusData } from 'hooks/useAppStatusData';
import NotificationSidebar from 'layout/NotificationSidebar';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setReturnRoute, toggleSettings, toggleThirdPartyLicenses } from 'store/reducers/uiSettingsSlice';
import { AppRoutes } from 'utilities/router/AppRoutes';

const licenseSlug = 'help:license';
const releaseChangeLogSlug = 'help:releaseChangeLog';
const dataUsageSlug = 'settings:userData';
const usersSettingsSlug = 'settings:users';
const rolesSettingsSlug = 'settings:roles';
const contractsSlug = 'settings:contracts';
const versionSlug = 'settings:version';
const notificationSettingsSlug = 'settings:notifications';
const documentationSlug = 'help:documentation';
const notificationsSlug = 'notifications';

type MenuItem = Pick<MenuItemType, 'label' | 'slug'>;

export type NavOutletContext = {
  releaseChangeLogOpen: boolean;
  onReleaseChangeLogOpenChange: (open: boolean) => void;
  onReleaseChangeLogAvailabilityChange: (available: boolean) => void;
};

// label included: a relabelled child (language switch) also needs the remount
function menuKey(items: MenuItem[], activeSlug: string) {
  const labelledSlugs = items.map(i => i.slug + '=' + i.label).join();
  return `${labelledSlugs}|${activeSlug}`;
}

function renderItems(items: MenuItem[], activeSlug: string) {
  return items.map(item => (
    <UxcMenuItem key={item.slug} slug={item.slug} label={item.label} current={activeSlug === item.slug} />
  ));
}

export default function Nav() {
  const ref = useRef<HTMLElement>(null);
  const value: NavbarContextType = useMemo(() => ({ navbarRef: ref }), [ref]);

  const { currentUser, logout } = useAuthContext();
  const navigate = useNavigate();

  const { downloadAndSaveFile } = useDownloadFileApi();

  const isSuperAdmin = currentUser?.roles['SuperAdmin'] ?? false;
  const [showCustomerContracts, setShowCustomerContracts] = useState(false);
  const appStatus = useAppStatusData();
  const [showReleaseChangeLog, setShowReleaseChangeLog] = useState(false);
  const [releaseChangeLogAvailable, setReleaseChangeLogAvailable] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { stage: noiseStage } = useNoiseConfig();
  const bellRef = useRef<HTMLUxcMenuItemElement>(null);
  const closeNotifications = useCallback(() => setShowNotifications(false), []);

  const { t } = useTranslation();
  const helpItems = useMemo<MenuItem[]>(
    () =>
      [
        { label: t('thirdPartyLicenses.title'), slug: licenseSlug },
        { label: t('releaseChangeLog.title'), slug: releaseChangeLogSlug },
        { label: t('help.documentation'), slug: documentationSlug },
      ].filter(item => item.slug !== releaseChangeLogSlug || releaseChangeLogAvailable),
    [t, releaseChangeLogAvailable]
  );

  const dispatch = useAppDispatch();
  // router pathname, not window.location.pathname: the latter still carries the basename
  const { pathname } = useLocation();
  const onMenuItemClick = useCallback(
    (item: CustomEvent<{ slug: string }>) => {
      function setReturnUrlAndNavigate(newUrl: string) {
        if (!pathname.startsWith(AppRoutes.Settings)) {
          dispatch(setReturnRoute(pathname));
        }
        void navigate(newUrl);
      }

      // Neo only collapses on outside click, close dropdown after leaf selection
      const clicked = item.target as HTMLElement | null;
      const topLevel = clicked?.parentElement?.closest<HTMLUxcMenuItemElement>('uxc-menu-item[level="0"]');
      if (topLevel) topLevel.expanded = false;

      const slug = item.detail.slug;
      if (slug === notificationsSlug) setShowNotifications(open => !open);
      if (slug === licenseSlug) dispatch(toggleThirdPartyLicenses());
      if (slug === releaseChangeLogSlug && releaseChangeLogAvailable) setShowReleaseChangeLog(true);
      if (slug === dataUsageSlug) dispatch(toggleSettings());
      if (slug === usersSettingsSlug) setReturnUrlAndNavigate(AppRoutes.SettingsUser);
      if (slug === rolesSettingsSlug) setReturnUrlAndNavigate(AppRoutes.SettingsRole);
      if (slug === contractsSlug) {
        // SuperAdmins get the full page; customers only a modal with their own contracts.
        if (isSuperAdmin) setReturnUrlAndNavigate(AppRoutes.SettingsContracts);
        else setShowCustomerContracts(true);
      }
      if (slug === versionSlug) setReturnUrlAndNavigate(AppRoutes.Versions);
      if (slug === notificationSettingsSlug) setReturnUrlAndNavigate(AppRoutes.SettingsNotifications);
      if (slug === documentationSlug)
        void downloadAndSaveFile('documentation', 'trumpf-easymodel-ai-documentation.pdf');
    },
    [dispatch, navigate, downloadAndSaveFile, isSuperAdmin, pathname, releaseChangeLogAvailable]
  );

  const isLicenseOpen = useAppSelector(s => s.uiSettings.showThirdPartyLicenses);
  const isSettingsOpen = useAppSelector(s => s.uiSettings.showSettings);
  let slug = '';
  if (isLicenseOpen) {
    slug = licenseSlug;
  } else if (showReleaseChangeLog) {
    slug = releaseChangeLogSlug;
  } else if (isSettingsOpen) {
    slug = dataUsageSlug;
  } else if (pathname === AppRoutes.SettingsUser) {
    slug = usersSettingsSlug;
  } else if (pathname === AppRoutes.SettingsRole) {
    slug = rolesSettingsSlug;
  } else if (pathname === AppRoutes.SettingsContracts) {
    slug = contractsSlug;
  } else if (pathname === AppRoutes.Versions) {
    slug = versionSlug;
  } else if (pathname === AppRoutes.SettingsNotifications) {
    slug = notificationSettingsSlug;
  }

  const settingsItems: MenuItem[] = [
    { label: t('settings.data.title'), slug: dataUsageSlug },
    { label: t('settings.contracts.title'), slug: contractsSlug },
    { label: t('settings.notifications.title'), slug: notificationSettingsSlug },
  ];

  if (isSuperAdmin) {
    settingsItems.push(
      { label: t('settings.users.title'), slug: usersSettingsSlug },
      { label: t('settings.roles.title'), slug: rolesSettingsSlug },
      { label: t('settings.versions.title'), slug: versionSlug }
    );
  }

  const displayName = `${currentUser?.firstName ?? ''} ${currentUser?.lastName ?? ''}`.trim();

  return (
    <>
      <section ref={ref} data-testid='app-nav'>
        <UxcAppHeaderNeo onUxMenuItemClick={onMenuItemClick}>
          <UxcAppLauncher slot='app-launcher' defaultlinks />
          <nav slot='secondary-nav'>
            {/* key: Neo menus slot children and compute current-child only on mount, remount on change */}
            <UxcAppHeaderSettingsMenu key={menuKey(settingsItems, slug)}>
              {renderItems(settingsItems, slug)}
            </UxcAppHeaderSettingsMenu>
            <UxcAppHeaderHelpMenu key={menuKey(helpItems, slug)}>{renderItems(helpItems, slug)}</UxcAppHeaderHelpMenu>
          </nav>
          <nav slot='user-actions'>
            {/* mount only with a stage: the item detects its icon slot on mount only, and the
                bell clobbers its own stage default when mounted without one */}
            {noiseStage && (
              <UxcMenuItem
                ref={bellRef}
                slug={notificationsSlug}
                label={t('notifications.title')}
                current={showNotifications}
              >
                <uxn-notification-bell slot='icon' stage={noiseStage} />
              </UxcMenuItem>
            )}
            {/* user-data slot must be filled on mount, else Neo shows a login button instead of logout */}
            {currentUser && (
              <UxcAppHeaderUserProfile key={currentUser.userName} onUxLogout={logout}>
                <div slot='user-data'>
                  <strong>{displayName || currentUser.userName}</strong>
                  {currentUser.email && <div>{currentUser.email}</div>}
                </div>
              </UxcAppHeaderUserProfile>
            )}
          </nav>
        </UxcAppHeaderNeo>
        <AppBanner banner={appStatus?.banner} />
        <InitializationBanner />
      </section>
      <NotificationSidebar open={showNotifications} onClose={closeNotifications} ignoreCloseRef={bellRef} />
      {showCustomerContracts && <CustomerContractsDialog open onClose={() => setShowCustomerContracts(false)} />}
      <NavbarContext.Provider value={value}>
        <Outlet
          context={
            {
              releaseChangeLogOpen: showReleaseChangeLog,
              onReleaseChangeLogOpenChange: setShowReleaseChangeLog,
              onReleaseChangeLogAvailabilityChange: setReleaseChangeLogAvailable,
            } satisfies NavOutletContext
          }
        />
      </NavbarContext.Provider>
    </>
  );
}
